import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { manifesto } from '../mockData';
import { GraduationCap, Users, Heart, UserCheck, Leaf, Smartphone, CheckCircle2 } from 'lucide-react';

const iconMap = {
  GraduationCap,
  Users,
  Heart,
  UserCheck,
  Leaf,
  Smartphone,
};

const Manifesto = () => {
  const { language, t } = useLanguage();
  const content = manifesto[language];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-party-red py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t({ en: 'Our Manifesto', bn: 'আমাদের ইশতেহার' })}
          </h1>
          <p className="text-xl text-red-100 max-w-3xl mx-auto">
            {t({
              en: 'Our vision and commitment for a progressive Bangladesh.',
              bn: 'একটি অগ্রগতিমুখী বাংলাদেশের জন্য আমাদের দৃষ্টিভঙ্গি এবং প্রতিশ্রুতি।',
            })}
          </p>
        </div>
      </section>

      {/* Vision Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t({ en: 'Our Vision', bn: 'আমাদের দৃষ্টিভঙ্গি' })}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">{content.vision}</p>
          </div>
        </div>
      </section>

      {/* Priorities */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            {t({ en: 'Our Priorities', bn: 'আমাদের অগ্রাধিকার' })}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {content.priorities.map((priority, idx) => {
              const Icon = iconMap[priority.icon];
              return (
                <Card key={idx} className="hover:shadow-2xl transition-all duration-300">
                  <CardHeader>
                    <div className="w-16 h-16 bg-party-green-100 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-party-green" />
                    </div>
                    <CardTitle className="text-xl">{priority.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">{priority.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Commitments */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            {t({ en: 'Our Commitments', bn: 'আমাদের প্রতিশ্রুতি' })}
          </h2>
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <div className="space-y-4">
                  {[
                    {
                      en: 'Restore public trust in democratic institutions through ethical leadership',
                      bn: 'নৈতিক নেতৃত্বের মাধ্যমে গণতান্ত্রিক প্রতিষ্ঠানে জনগণের আস্থা পুনরুদ্ধার',
                    },
                    {
                      en: 'Ensure equal development opportunities in both urban and rural areas',
                      bn: 'শহর ও গ্রামে সমান উন্নয়নের সুযোগ নিশ্চিত করা',
                    },
                    {
                      en: 'Bridge the gap between tradition and modern needs',
                      bn: 'ঐতিহ্য এবং আধুনিক চাহিদার মধ্যে সেতুবন্ধন তৈরি',
                    },
                    {
                      en: 'Implement skill-based, inclusive, and technology-driven education',
                      bn: 'দক্ষতাভিত্তিক, অন্তর্ভুক্তিমূলক ও প্রযুক্তিনির্ভর শিক্ষা বাস্তবায়ন',
                    },
                    {
                      en: 'Support startups, technical training, and entrepreneurship for youth',
                      bn: 'তরুণদের জন্য স্টার্টআপ, কারিগরি প্রশিক্ষণ এবং উদ্যোক্তার সহায়তা',
                    },
                    {
                      en: 'Provide affordable, accessible, and rural-focused healthcare',
                      bn: 'সুলভ, সহজলভ্য এবং গ্রামীণভিত্তিক স্বাস্থ্যসেবা প্রদান',
                    },
                    {
                      en: 'Guarantee equal representation and security for women in all sectors',
                      bn: 'সকল ক্ষেত্রে নারীদের জন্য সমান প্রতিনিধিত্ব এবং নিরাপত্তা নিশ্চিত করা',
                    },
                    {
                      en: 'Promote sustainable agriculture and renewable energy adoption',
                      bn: 'টেকসই কৃষি এবং নবায়নযোগ্য শক্তির ব্যবহার প্রচার',
                    },
                  ].map((commitment, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-6 w-6 text-party-green flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700 leading-relaxed">{t(commitment)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Manifesto;